/**
 * Pure helpers for the v2 canvas-item contract (storyCanvas.types.ts,
 * "Canvas items (v2)").
 */
import { withBuiltInPalette } from "../chartStudio/applyChartPalette";
import { withBarValueLabels } from "../../components/molecules/VegaChart/vegaChartSpec";
import { rawColors } from "../../design-system/tokens/colors";
import type {
  ChartWidget,
  ReportNativeChart,
  StoryWidget,
  TileFilteredBy,
  TileUnavailable,
} from "../dataStories/generateStory.types";
import { readTitleText } from "../chartStudio/readChartFormat";
import { buildManifestPlaceholder, mapCanvasWidgetOrNull, resolveStoryVisibilityFlags } from "./storyCanvasMappers";
import type { StoryPath } from "../../contexts/StoryPathContext";
import { toFilterChipLabel, toRelativePeriodText } from "./storyFiltersMappers";
import type { StoryFilterApi } from "./storyFilters.types";
import { toCanvasPosition } from "./storyGridUnits";
import type {
  BindingApi,
  CanvasItemFullApi,
  CanvasItemListResponseApi,
  CanvasItemPatchRequestApi,
  CanvasItemRowTypeApi,
  CanvasItemStubApi,
  CanvasItemSummaryApi,
  CanvasItemType,
  CanvasWidgetApi,
  FieldBindingApi,
  FormatApi,
  StoryTextApi,
} from "./storyCanvas.types";

type PlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is PlainObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * RFC 7396 JSON merge-patch, the PATCH `.../items/:itemId` semantics: objects
 * merge key by key, `null` removes a key, and anything else, arrays included,
 * replaces the target outright. One implementation serves both the mock
 * server and the optimistic cache write, so the two cannot disagree on what a
 * PATCH does. Returns new objects and never mutates either argument.
 */
export const mergePatch = <T>(target: T, patch: unknown): T => {
  if (!isPlainObject(patch)) return patch as T;
  const merged: PlainObject = isPlainObject(target) ? { ...target } : {};
  Object.entries(patch).forEach(([key, value]) => {
    if (value === null) {
      delete merged[key];
    } else if (value !== undefined) {
      merged[key] = mergePatch(merged[key], value);
    }
  });
  return merged as T;
};

/**
 * `?detail=summary` → the ids Chart Studio can open on. The server computes
 * `editable` (true only for `chart_2`/`kpi_2`); the frontend never infers it
 * from `item_type`.
 */
export const toEditableItemIds = (
  response: CanvasItemListResponseApi,
): ReadonlySet<string> =>
  new Set(response.items.filter((item) => item.editable).map((item) => item.id));

/**
 * The item as the user sees it mid-save: the server's copy with every PATCH
 * still in flight merged on, in the order they were sent. `spec` is left as
 * the server's, because the chart only ever redraws from a spec the server
 * compiled.
 */
export const applyPendingPatches = (
  item: CanvasItemFullApi,
  patches: readonly CanvasItemPatchRequestApi[],
): CanvasItemFullApi =>
  patches.reduce<CanvasItemFullApi>(
    (current, body) => mergePatch(current, { binding: body.binding, format: body.format }),
    item,
  );

/**
 * The widget header title `format.title` asks for. The chart title is drawn
 * by the widget header, not the spec, so this is the one format key the
 * canvas applies itself. A format that says nothing keeps the widget's own.
 */
export const toItemHeaderTitle = (
  format: FormatApi | null | undefined,
  fallback: string,
): string => {
  if (format?.title?.show === false) return "";
  return format?.title?.text ?? fallback;
};

const labelledSpecs = new WeakMap<object, unknown>();

/**
 * The item's palette-painted spec. A plain single-series bar chart whose format leaves "direct
 * values" unset also gets its value above each bar (the F07 design's default); a format that sets
 * them either way is the server's call and is left alone. Memoised per spec object.
 */
const toTileSpec = <T extends { spec?: unknown; spec_kind?: string | null; format?: FormatApi | null }>(
  item: T,
  storyPaletteId?: string | null,
): unknown => {
  const spec = withBuiltInPalette(item as never, storyPaletteId) as unknown;
  if (!isPlainObject(spec) || item.spec_kind === "vega" || item.format?.data_values != null) return spec;
  if (!labelledSpecs.has(spec)) labelledSpecs.set(spec, withBarValueLabels(spec as never, rawColors.text.mediumEmphasis));
  return labelledSpecs.get(spec);
};

/**
 * The canvas's view of the charts Chart Studio has loaded: each draws its
 * server-compiled v2 spec under the title its format sets. Every other widget
 * is returned as the same object, so an unloaded chart never re-renders.
 */
export const applyCanvasItems = (
  widgets: StoryWidget[],
  itemsById: Readonly<Record<string, CanvasItemFullApi>>,
  storyPaletteId?: string | null,
): StoryWidget[] =>
  widgets.map((widget) => {
    const item = widget.type === "chart" ? itemsById[widget.id] : undefined;
    if (!item || widget.type !== "chart") return widget;
    const spec = (toTileSpec(item, storyPaletteId) as typeof widget.spec | undefined) ?? widget.spec;
    const specKind = item.spec ? item.spec_kind ?? undefined : widget.specKind;
    const title = toItemHeaderTitle(item.format, widget.title);
    if (spec === widget.spec && specKind === widget.specKind && title === widget.title) return widget;
    return { ...widget, spec, specKind, title };
  });

/**
 * The summary list with each moved tile at the position a bulk move returned
 * for it. Rows the move didn't return, or returned with no position, keep
 * their own.
 */
export const withMovedPositions = (
  list: CanvasItemListResponseApi,
  moved: readonly CanvasItemStubApi[],
): CanvasItemListResponseApi => {
  const byId = new Map(moved.map((stub) => [stub.item_id, stub.position]));
  return {
    ...list,
    items: list.items.map((item) => {
      const position = byId.get(item.id);
      if (!position) return item;
      return {
        ...item,
        pos_x: position.x,
        pos_y: position.y,
        pos_w: position.w,
        pos_h: position.h,
        pos_cols: position.cols,
        // A server that predates the flag answers without it: keep what the row had.
        pos_h_fixed: position.h_fixed ?? item.pos_h_fixed,
      };
    }),
  };
};

/**
 * True for the two new-architecture types. They have no v1 widget endpoint: the v1 `mapCanvasWidget`
 * throws on them, so a loader must route them to the item GET instead.
 */
export const isItemEndpointType = (type: string): type is "chart_2" | "kpi_2" =>
  type === "chart_2" || type === "kpi_2";

/** The tile type an item draws in: `chart_2` / `kpi_2` share the `chart` / `kpi` tiles. */
export const toTileType = (type: CanvasItemRowTypeApi): CanvasItemType =>
  type === "chart_2" ? "chart" : type === "kpi_2" ? "kpi" : type;

/**
 * A summary row as the stub its tile is placed from. `chart_2` / `kpi_2` draw
 * in the same tiles as `chart` / `kpi`. The summary has no `visibility`, so a
 * chart's companions stay hidden.
 */
export const toCanvasItemStub = ({
  id,
  item_type: itemType,
  pos_x: x,
  pos_y: y,
  pos_w: w,
  pos_h: h,
  pos_cols: cols,
  pos_h_fixed: heightFixed,
  status,
}: CanvasItemSummaryApi): CanvasItemStubApi => ({
  item_id: id,
  type: toTileType(itemType),
  position: {
    x,
    y,
    w,
    h,
    ...(cols == null ? {} : { cols }),
    ...(heightFixed == null ? {} : { h_fixed: heightFixed }),
  },
  status,
});

export interface ToCanvasItemWidgetOptions {
  /** The story's filter rows, to sort the item's applied filters into `filteredBy`. Absent: no "Filtered by" line. */
  filters?: readonly StoryFilterApi[];
  /** The story's path. Absent is "old": the v1 default (companions hidden until inserted). */
  path?: StoryPath;
}

/**
 * One canvas tile: the empty tile of the row's type until its GET lands, then
 * either a chart_2/kpi_2's server-compiled `spec` (rows inline) under the
 * title its `format` sets — or, when the format sets none, the spec's own
 * title — or, for every other (legacy) type, its `content` mapped through the
 * same `mapCanvasWidgetOrNull` the old v1 widget endpoint already uses, so the two
 * flows render through identical, already-proven view-model logic. ChartWidget
 * never lets Vega draw the spec title itself, so the header is the only place
 * it can show. A chart_2/kpi_2 the server couldn't draw (`spec_status`
 * `failed`) says so under its title, with its notices.
 */
export const toCanvasItemWidget = (
  summary: CanvasItemSummaryApi,
  item: CanvasItemFullApi | undefined,
  options: ToCanvasItemWidgetOptions = {},
): StoryWidget => {
  const stub = toCanvasItemStub(summary);
  const tile = buildManifestPlaceholder(stub);
  // On the canvas grid: the summary's x / w are on the server's grid.
  const position = stub.position ? toCanvasPosition(stub.position) : undefined;
  const widget = item?.content ? mapCanvasWidgetOrNull(toWidgetContent(item.content, item.tool_filters)) : null;
  if (widget) {
    const native = item ? toReportNative(widget, item) : undefined;
    const shown = native ? { ...widget, native } : widget;
    // A KPI says which filters and period it reads, as a chart tile does (the memo tells namesakes apart by it).
    const kpiFilters = shown.type === "kpi" && item ? { filteredBy: toFilteredBy(item, options.filters) } : {};
    return { ...(item?.text ? withReaderText(shown, item.text) : shown), ...kpiFilters, position };
  }
  if (item?.spec_status === "failed" && (tile.type === "chart" || tile.type === "kpi")) {
    const title = toItemHeaderTitle(item.format, "");
    const unavailable = toTileUnavailable(item);
    return tile.type === "chart" ? { ...tile, position, title, unavailable } : { ...tile, position, name: title, unavailable };
  }
  if (tile.type === "kpi" && item) return { ...tile, position, filteredBy: toFilteredBy(item, options.filters) };
  if (tile.type !== "chart" || !item?.spec) return { ...tile, position };
  return {
    ...tile,
    position,
    spec: toTileSpec(item) as typeof tile.spec,
    specKind: item.spec_kind ?? undefined,
    title: toItemHeaderTitle(
      item.format,
      readTitleText(isPlainObject(item.spec) ? item.spec.title : undefined) ?? "",
    ),
    ...toCompanionFields(item, options.path),
    filteredBy: toFilteredBy(item, options.filters),
  };
};

/** A BI view drawn as our own chart, for a filter its tool can't take: the server sends a spec beside the embed. */
const toReportNative = (widget: StoryWidget, item: CanvasItemFullApi): ReportNativeChart | undefined => {
  const isReport = widget.type === "report" || widget.type === "ingested_widget" || widget.type === "captured_widget";
  const spec = isReport ? withBuiltInPalette(item) : undefined;
  if (!spec) return undefined;
  const reasons = (item.notices ?? []).filter((notice) => notice.kind === "replaced").map((notice) => notice.text);
  return { spec, specKind: item.spec_kind ?? "vega-lite", reasons };
};

/** A BI view's content with the tool filters its item read carries beside it. */
const toWidgetContent = (content: CanvasWidgetApi, toolFilters: CanvasItemFullApi["tool_filters"]): CanvasWidgetApi =>
  toolFilters != null &&
  (content.type === "report" || content.type === "ingested_widget" || content.type === "captured_widget")
    ? { ...content, tool_filters: toolFilters }
    : content;

export const TILE_CANT_BE_DRAWN = "This can't be drawn";
export const NO_REASON = "It can't be drawn right now.";

/** A tile with nothing to draw, and why: its notices, or a plain line when it has none. */
export const toTileUnavailable = (item: Pick<CanvasItemFullApi, "notices">): TileUnavailable => {
  const reasons = Array.from(new Set((item.notices ?? []).map((notice) => notice.text).filter(Boolean)));
  return { title: TILE_CANT_BE_DRAWN, reasons: reasons.length > 0 ? reasons : [NO_REASON] };
};

/**
 * The text Story Manager writes on a chart_2 item (`ai_summary`, `narrative`) and whether it
 * shows. A flag the server sets is honoured on both paths. With no flags at all, a new-path item
 * shows the text it has for that slot (Story Manager writes it to be read). An old-path item keeps
 * v1's default: hidden until the user inserts it, so the tile stays full width.
 */
export const toCompanionFields = (
  item: Pick<CanvasItemFullApi, "ai_summary" | "ai_summary_state" | "narrative" | "visibility">,
  path: StoryPath = "old",
): Pick<ChartWidget, "aiSummary" | "aiSummaryState" | "narrative" | "isSummaryVisible" | "isNarrativeVisible"> => {
  const aiSummary = item.ai_summary ? { title: item.ai_summary.title, body: item.ai_summary.body } : undefined;
  const narrative = item.narrative?.trim() ? item.narrative : undefined;
  const hasFlags = item.visibility != null && Object.keys(item.visibility).length > 0;
  const flags = resolveStoryVisibilityFlags(item.visibility);
  return {
    aiSummary,
    aiSummaryState: item.ai_summary_state ?? undefined,
    narrative,
    isSummaryVisible: hasFlags || path === "old" ? flags.isSummaryVisible : aiSummary !== undefined,
    isNarrativeVisible: hasFlags || path === "old" ? flags.isNarrativeVisible : narrative !== undefined,
  };
};

/**
 * Where a chart's applied filters come from: story-wide ones, a cross-filter set on another
 * chart, and the chart's own (local, drill-down, baseline) as chip labels. `undefined` when no
 * filter reaches it or when the story's filter rows are not at hand to classify the ids.
 */
export const toFilteredBy = (
  item: Pick<CanvasItemFullApi, "id" | "filters_applied">,
  filters?: readonly StoryFilterApi[],
): TileFilteredBy | undefined => {
  if (!filters || !item.filters_applied?.length) return undefined;
  const byId = new Map(filters.map((filter) => [filter.id, filter]));
  const result: TileFilteredBy = { global: false, otherChart: false, own: [] };
  item.filters_applied.forEach((id) => {
    const filter = byId.get(id);
    if (!filter) return;
    if (filter.kind === "global") result.global = true;
    else if (filter.kind === "cross") result.otherChart = true;
    else if (filter.canvas_item_id === item.id) result.own.push(toFilterChipLabel(filter).label);
  });
  const period = toTilePeriod(item, filters);
  if (period) result.period = period;
  return result.global || result.otherChart || result.own.length > 0 ? result : undefined;
};

/** Which filter wins on a tile's date: the tile's own, then a cross-filter, then the story's. */
const dateFilterRank = (filter: StoryFilterApi, itemId: string): number => {
  if (filter.canvas_item_id === itemId && filter.kind !== "cross") return 0;
  return filter.kind === "cross" ? 1 : filter.kind === "global" ? 2 : 3;
};

/**
 * The rolling period of the date filter in effect on a tile (tile over cross over global), in the chip's
 * words; null when no date filter reaches it or the winning one is fixed dates.
 */
export const toTilePeriod = (
  item: Pick<CanvasItemFullApi, "id" | "filters_applied">,
  filters?: readonly StoryFilterApi[],
): string | null => {
  const applied = new Set(item.filters_applied ?? []);
  const dates = (filters ?? [])
    .filter((filter) => applied.has(filter.id) && filter.value_type === "temporal" && filter.kind !== "drill_down")
    .sort((a, b) => dateFilterRank(a, item.id) - dateFilterRank(b, item.id));
  const winner = dates[0];
  return winner?.comparator === "relative_date" ? toRelativePeriodText(winner.value) || null : null;
};

const RECOMMENDATIONS = "Recommendations";
const MAX_INSIGHTS = 5;
const BULLET = /^\s*(?:[-*+]|\d{1,2}[.)])\s+/;

/**
 * A new-path text tile with this reader's words and its mark. The tile was
 * copied with the Story's own words when it was last copied; the item read's
 * are filled from the reader's rows and current after a rewrite. Withheld or
 * unwritten text shows no words.
 */
export const withReaderText = (widget: StoryWidget, text: StoryTextApi): StoryWidget => {
  const textMark = {
    state: text.state,
    edited: text.edited,
    newerItems: text.newer_items,
    note: text.note ?? undefined,
  };
  const body = text.body ?? "";
  switch (widget.type) {
    case "summary":
    case "narrative":
      return { ...widget, body, textMark };
    case "insight": {
      const bullets = body
        .split("\n")
        .map((line) => line.replace(BULLET, "").trim())
        .filter(Boolean)
        .slice(0, MAX_INSIGHTS);
      return { ...widget, bullets, sections: undefined, textMark };
    }
    case "text":
      if (text.slot !== "recommendation") return { ...widget, textMark };
      return {
        ...widget,
        content: body ? `${RECOMMENDATIONS}\n\n${body}` : RECOMMENDATIONS,
        textMark,
      };
    default:
      return widget;
  }
};

/**
 * Every `model_field_id` a binding uses — X, each Y, Color by and both
 * trellis sides — for the Data models panel's "used in this chart" flag.
 */
export const toBoundFieldIds = (
  binding: BindingApi | null | undefined,
): ReadonlySet<string> => {
  if (!binding) return new Set();
  const slots: Array<FieldBindingApi | null | undefined> = [
    binding.x,
    ...(binding.y ?? []),
    binding.color_by,
    binding.trellis?.horizontal,
    binding.trellis?.vertical,
  ];
  return new Set(
    slots.flatMap((slot) => (slot ? [slot.field.model_field_id] : [])),
  );
};
